/* Copyright (c) 2020-present CloudIO Inc */

import { toOptions } from 'componentregistry/utils/ManagerUtils';
import { page as SamplePage } from '../../help/sample_pages/SamplePageSimple';
import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { ComponentManagerFn, Item } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './MetricBox';

export const metricSamplePage: typeof SamplePage & {
  child: Item<'MetricColorAndIcon'>;
} = {
  ...SamplePage,
  child: {
    children: [],
    itemId: 'child',
    parent: 'chartjs',
    props: {
      caseValue: 'X',
      icon: { iconName: 'analytics', iconType: 'light' },
      color: 'primary',
    },
    type: 'MetricColorAndIcon',
  },
  flexItem: {
    ...SamplePage.flexItem,
    props: {
      backgroundColor: 'default',
      padding: { all: 20 },
    },
  },
};

const MetricBoxManager: ComponentManagerFn<'MetricBox'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'MetricBox'>(platform, 'MetricBox'),
    type: 'MetricBox',
    name: 'Metric Box',
    isChildAllowed: (page, item, childType) =>
      (
        [
          'EventSubscription',
          'MetricColorAndIcon',
          'PulseTip',
          'TriggerOnClick',
        ] as ItemType[]
      ).includes(childType),
    defaultValue,
    props: {
      title: {
        label: 'Title',
        required: true,
        type: 'expression',
        returnType: async () => 'string',
      },
      value: {
        label: 'Value',
        type: 'expression',
        required: true,
        returnType: async () => 'string',
      },
      trend: {
        label: 'Trend',
        type: 'expression',
        returnType: async () => 'number',
        placeholder: 'e.g. 1, 0 or -1',
        helpText:
          'Return a positive value for an up trend, negetive for a down trend or zero for a flat trend.',
      },
      trendDescription: {
        label: 'Trend Description',
        type: 'expression',
        returnType: async () => 'string',
        placeholder: 'e.g. 20% compared to last month',
      },
      colorAndIconSwitchCondition: {
        label: 'Switch Expression',
        type: 'expression',
        returnType: async () => 'string',
        helpText:
          'Must return a string value that will be used to find a matching Metric Color & Icon Case',
      },
      metricStyle: {
        label: 'Style',
        type: 'select',
        options: toOptions(['Alpha', 'Beta', 'Gamma', 'Delta']),
        required: true,
      },
      minWidth: {
        label: 'Minimum Width',
        type: 'number',
        max: 400,
        step: 40,
      },
    },
    getDisplayText: (item) => item.props.title.value,
    category: 'Dashboard',
    icon: { iconName: 'trending_up', iconType: 'cloudio' },
    excludeChildren: true,
    description: `**Overview**<br/>
The Metric Box is a specialized dashboard component designed to present Key Performance Indicators (KPIs) with immediate visual context. Unlike a generic text block, the Metric Box standardizes the display of a metric, its label, and its performance trend into a cohesive, styled widget.
It is best used to highlight critical numbers—such as "Total Revenue," "Active Users," or "Pending Tickets"—at the top of a dashboard to give users a high-level snapshot of system health.

**1. Core Content & Values**<br/>
The foundation of the component is the metric itself. These fields usually rely on dynamic expressions to fetch data from your backend.
* **Title:** The label describing the metric (e.g., "Monthly Sales").
* **Value:** The primary numeric or text value to display (e.g., "$50,000").
* **Minimum Width:** Ensures the box maintains a readable size even on small screens. Setting this prevents the layout from crushing the metric content.

**2. Trend Visualization**<br/>
A raw number often lacks context. The Metric Box includes built-in logic to visualize performance direction (growth vs. decline).
* **Trend:** A numeric expression used to calculate the direction arrow.
    * **Positive Value (> 0):** Renders an "Up" arrow (typically indicating growth).
    * **Negative Value (< 0):** Renders a "Down" arrow (typically indicating decline).
    * **Zero (0):** Renders a "Flat" dash.
* **Trend Description:** Optional text displayed alongside the trend icon to explain the context (e.g., "vs. Last Month" or "+5% increase").

**3. Visual Styles (Variants)**<br/>
You can change the structural layout of the box to fit different dashboard densities using the Style property.
* **Options:** Alpha, Beta, Gamma, Delta.
* **Usage:** These variants rearrange the position of the icon, value, and title (e.g., placing the icon on the left vs. the top) without requiring manual CSS.

**4. Dynamic States (Color & Icons)**<br/>
The Metric Box supports advanced conditional styling, allowing it to change color based on the data (e.g., turning Red if a server is "Down" or Green if "Active").
* **How to configure:**
    * **Switch Expression (Parent):** In the Metric Box properties, define an expression that returns a status string (e.g., return "Critical" if value > 90).
    * **Metric Color & Icon (Child):** Add this child component to the Metric Box.
    * **Case Matching:** In the child component, set the Case Value to match your status string (e.g., "Critical"). Then, select the specific Icon and Color for that state.
* **Note:** You can add multiple "Metric Color & Icon" children to handle different states (e.g., one for "Success", one for "Warning", one for "Error").`,
    helpRenders: [
      {
        title: 'Metric Box',
        description:
          'Below is an example with hard coded values. You can use expression to set dynamic values.',
        page: metricSamplePage,
        exampleId: 'e1',
        clearParentChildren: true,
        height: 280,
        visibleProps: ['title', 'value', 'trend', 'trendDescription'],
        item: {
          children: ['child'],
          itemId: 'chartjs',
          parent: 'columnPanel1',
          props: {
            title: { type: 'text', value: 'Processed' },
            value: { type: 'text', value: '50,000' },
            trend: { type: 'text', value: '1' },
            trendDescription: {
              type: 'text',
              value: '10% compared to last month',
            },
            colorAndIconSwitchCondition: { type: 'text', value: 'X' },
            metricStyle: 'Alpha',
            minWidth: 300,
          },
          type: 'MetricBox',
        },
      },
      {
        title: 'Style',
        description: 'Try changing the syle of the Metric Box',
        page: metricSamplePage,
        exampleId: 'e2',
        clearParentChildren: true,
        height: 280,
        visibleProps: ['metricStyle'],
        item: {
          children: ['child'],
          itemId: 'chartjs',
          parent: 'columnPanel1',
          props: {
            title: { type: 'text', value: 'Processed' },
            value: { type: 'text', value: '50,000' },
            trend: { type: 'text', value: '1' },
            trendDescription: {
              type: 'text',
              value: '10% compared to last month',
            },
            colorAndIconSwitchCondition: { type: 'text', value: 'X' },
            metricStyle: 'Beta',
            minWidth: 300,
          },
          type: 'MetricBox',
        },
      },
    ],
  };
};

export default MetricBoxManager;
